export type Bilingual = {
  en: string
  fr: string
}

export const platformIntro = {
  eyebrow: { en: 'The platform', fr: 'La plateforme' },
  headlineLead: { en: 'One platform.', fr: 'Une seule plateforme.' },
  headlineBefore: { en: 'Three ', fr: 'Trois ' },
  headlineAccent: { en: 'experiences.', fr: 'expériences.' },
  intro: {
    en: 'Intelligent website, resident portal and mobile app. The same identity, the same services, the same intelligence — across every channel.',
    fr: 'Site web intelligent, portail citoyen et application mobile. La même identité, les mêmes services, la même intelligence — sur tous les canaux.',
  },
}

export type ExperienceId = 'website' | 'portal' | 'app'

export type Experience = {
  id: ExperienceId
  label: Bilingual
  headline: Bilingual
  body: Bilingual
  footer: Bilingual
}

export const experiences: Experience[] = [
  {
    id: 'website',
    label: { en: 'Intelligent website', fr: 'Site web intelligent' },
    headline: {
      en: 'Inform. Understand. Act.',
      fr: 'Informer. Comprendre. Agir.',
    },
    body: {
      en: 'A complete web experience where ATNA guides people through content, services and actions.',
      fr: 'Une expérience web complète où ATNA accompagne l’usager à travers les contenus, les services et les actions.',
    },
    footer: {
      en: 'Content · Services · Embedded agentic layer',
      fr: 'Contenus · Services · IA agentique intégrée',
    },
  },
  {
    id: 'portal',
    label: { en: 'Resident portal', fr: 'Portail citoyen' },
    headline: {
      en: 'A space for their services.',
      fr: 'Un espace pour leurs services.',
    },
    body: {
      en: 'A secure portal with access to requests, payments, notices, documents and preferences.',
      fr: 'Un portail sécurisé donnant accès aux demandes, paiements, avis, documents et préférences.',
    },
    footer: {
      en: 'Secure · Personalized · Always current',
      fr: 'Sécurisé · Personnalisé · Toujours à jour',
    },
  },
  {
    id: 'app',
    label: { en: 'Mobile app', fr: 'Application mobile' },
    headline: {
      en: 'Services in their pocket.',
      fr: 'Les services dans leur poche.',
    },
    body: {
      en: 'A simple, secure mobile app to stay connected at all times.',
      fr: 'Une application mobile simple et sécurisée pour rester connecté en tout temps.',
    },
    footer: {
      en: 'Quick access · Digital card · Real-time notices',
      fr: 'Accès rapide · Carte numérique · Avis en temps réel',
    },
  },
]

/** Copy rendered inside the three interface mockups. */
export const mockupCopy = {
  cityName: { en: 'Sainte-Rive', fr: 'Sainte-Rive' },
  cityTagline: { en: 'Living here', fr: 'Vivre ici' },
  cityTaglineMobile: { en: 'Living here, anywhere', fr: 'Vivre ici, partout' },
  nav: [
    { en: 'My city', fr: 'Ma ville' },
    { en: 'Culture and leisure', fr: 'Culture et loisirs' },
    { en: 'Services', fr: 'Services' },
  ],
  webHeadline: {
    en: 'What would you like to do today?',
    fr: 'Qu’avez-vous envie de faire aujourd’hui ?',
  },
  askPlaceholder: { en: 'Ask ATNA…', fr: 'Demander à ATNA…' },
  askSuggestions: [
    { en: 'Things to do this weekend', fr: 'Sortir cette fin de semaine' },
    { en: 'An activity near me', fr: 'Une activité près de moi' },
    { en: 'Renew a permit', fr: 'Renouveler un permis' },
  ],
  weather: {
    value: { en: '22 °C · Sunny', fr: '22 °C · Ensoleillé' },
    caption: {
      en: 'A beautiful day to enjoy your city.',
      fr: 'Une belle journée pour profiter de votre ville.',
    },
  },
  events: [
    {
      title: { en: 'Public market', fr: 'Marché public' },
      when: { en: 'Today', fr: 'Aujourd’hui' },
      image:
        'https://cdn.magicpatterns.com/patterns/generated-images/b8ee9583-e429-45ec-be27-b2ac7bca63b3.jpg',
    },
    {
      title: { en: 'Cinema in the park', fr: 'Cinéma au parc' },
      when: { en: 'Tonight', fr: 'Ce soir' },
      image:
        'https://cdn.magicpatterns.com/patterns/generated-images/aacbb1d1-93a7-4f41-b429-bb19a7a1440a.jpg',
    },
    {
      title: { en: 'Family swimming', fr: 'Baignade familiale' },
      when: { en: 'Saturday', fr: 'Samedi' },
      image:
        'https://cdn.magicpatterns.com/patterns/generated-images/5811a627-ff2d-4bd0-ae55-cc288391e398.jpg',
    },
  ],
  webShortcuts: [
    { en: 'My space', fr: 'Mon espace' },
    { en: 'Leisure', fr: 'Loisirs' },
    { en: 'Environment', fr: 'Environnement' },
    { en: 'Transport', fr: 'Transport' },
    { en: 'Library', fr: 'Bibliothèque' },
    { en: 'Permits', fr: 'Permis' },
  ],
  portal: {
    title: { en: 'My space', fr: 'Mon espace' },
    personName: { en: 'Marie Tremblay', fr: 'Marie Tremblay' },
    personRole: { en: 'Resident', fr: 'Citoyenne' },
    items: [
      { label: { en: 'My requests', fr: 'Mes demandes' }, badge: '2' },
      { label: { en: 'My payments', fr: 'Mes paiements' } },
      { label: { en: 'My activities', fr: 'Mes activités' } },
      { label: { en: 'My documents', fr: 'Mes documents' } },
      { label: { en: 'My digital card', fr: 'Ma carte numérique' } },
      { label: { en: 'My notifications', fr: 'Mes notifications' }, badge: '3' },
      { label: { en: 'My preferences', fr: 'Mes préférences' } },
    ],
  },
  app: {
    tiles: [
      { en: 'Digital card', fr: 'Carte numérique' },
      { en: 'My requests', fr: 'Mes demandes' },
      { en: 'My payments', fr: 'Mes paiements' },
      { en: 'Activities', fr: 'Activités' },
      { en: 'Waste collection', fr: 'Collectes' },
      { en: 'Contact us', fr: 'Nous joindre' },
    ],
    alertTitle: { en: 'Important notice', fr: 'Avis important' },
    alertBody: {
      en: 'Riverside Park closes today at 2 p.m.',
      fr: 'Fermeture du parc riverain aujourd’hui à 14 h',
    },
    tabs: [
      { en: 'Home', fr: 'Accueil' },
      { en: 'Services', fr: 'Services' },
      { en: 'Card', fr: 'Carte' },
      { en: 'Profile', fr: 'Profil' },
    ],
  },
}

export const foundation = {
  eyebrow: {
    en: 'The BCITI | ATNA foundation',
    fr: 'La fondation BCITI | ATNA',
  },
  statement: [
    { en: 'One identity.', fr: 'Une identité.' },
    { en: 'One context.', fr: 'Un contexte.' },
    { en: 'Connected services.', fr: 'Des services connectés.' },
  ],
  body: {
    en: 'A shared infrastructure that connects people, data, systems and intelligence to do more with the same resources.',
    fr: 'Une infrastructure commune qui relie les personnes, les données, les systèmes et l’intelligence pour faire plus avec les mêmes ressources.',
  },
  pillars: [
    {
      id: 'identity',
      icon: 'identity' as const,
      title: { en: 'Identity & trust', fr: 'Identité et confiance' },
      lines: [
        { en: 'Single ID · Card', fr: 'Identifiant unique · Carte' },
        { en: 'Profile · Consents', fr: 'Profil · Consentements' },
        { en: 'SSO / 2FA', fr: 'Authentification unique / A2F' },
      ],
    },
    {
      id: 'communications',
      icon: 'message' as const,
      title: { en: 'Communications', fr: 'Communications' },
      lines: [
        { en: 'Notices · Emergency', fr: 'Avis · Urgence' },
        { en: 'Personalization', fr: 'Personnalisation' },
        { en: 'Web · Mobile · SMS · Email', fr: 'Web · Mobile · SMS · Courriel' },
      ],
    },
    {
      id: 'requests',
      icon: 'requests' as const,
      title: { en: 'Requests & services', fr: 'Requêtes et services' },
      lines: [
        { en: 'Requests · Routing', fr: 'Requêtes · Routage' },
        { en: 'Real-time tracking', fr: 'Suivi en temps réel' },
        { en: 'Dashboards', fr: 'Tableaux de bord' },
      ],
    },
    {
      id: 'integrations',
      icon: 'data' as const,
      title: { en: 'Integrations & data', fr: 'Intégrations et données' },
      lines: [
        { en: 'APIs · Systems', fr: 'API · Systèmes' },
        { en: 'Transactions', fr: 'Transactions' },
        { en: 'Security & traceability', fr: 'Sécurité et traçabilité' },
      ],
    },
    {
      id: 'atna',
      icon: 'brain' as const,
      title: { en: 'ATNA intelligence', fr: 'Intelligence ATNA' },
      lines: [
        { en: 'Understand · Verify', fr: 'Comprendre · Vérifier' },
        { en: 'Orchestrate · Recommend', fr: 'Orchestrer · Recommander' },
        { en: 'Execute', fr: 'Exécuter' },
      ],
    },
  ],
  expansion: {
    en: 'Then activate the capabilities you need: permits, smart water, waste collection, payments, access, analytics and more.',
    fr: 'Puis activez les modules dont vous avez besoin : permis, eau intelligente, collectes, paiements, accès, analytique et plus encore.',
  },
}

export const platformClosing = {
  lead: {
    en: 'One platform, not a project.',
    fr: 'Une plateforme, pas un projet.',
  },
  accent: {
    en: 'Start simply. Add without rebuilding.',
    fr: 'Commencez simplement. Ajoutez sans reconstruire.',
  },
  caption: {
    en: 'Real results. At your pace.',
    fr: 'Des résultats concrets. À votre rythme.',
  },
}
